import React, { useEffect, useState } from "react";
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "sonner";
import {
  COUNTRY_LABELS,
  SELLING_COUNTRIES,
  currencyForCountry,
  majorToMinor,
  minorToMajor,
} from "@/constants/billingCatalog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = (path) => `${BACKEND_URL}/api/admin/tariffs${path}`;

function apiErrorDetail(err, fallback = "Request failed") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d) && d.length) {
    const first = d[0];
    if (typeof first === "string") return first;
    if (first?.msg) return String(first.msg);
  }
  return fallback;
}

function authHeaders() {
  return { Authorization: `Bearer ${Cookies.get("token")}` };
}

const AdminTariffs = () => {
  const [tariffs, setTariffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [detailTab, setDetailTab] = useState("edit");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [badge, setBadge] = useState("");
  const [credits, setCredits] = useState("");
  const [featuresText, setFeaturesText] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [status, setStatus] = useState("active");
  const [priceDrafts, setPriceDrafts] = useState({});
  const [rowSaving, setRowSaving] = useState(null);

  function emptyPriceDraft() {
    return { amount: "", stripePriceId: "", status: "draft" };
  }

  function patchDraft(code, patch) {
    setPriceDrafts((prev) => ({
      ...prev,
      [code]: { ...(prev[code] || emptyPriceDraft()), ...patch },
    }));
  }

  function draftsFromTariff(tariff) {
    const drafts = {};
    for (const code of SELLING_COUNTRIES) {
      const row = (tariff?.prices || []).find((p) => p.country === code);
      drafts[code] = row
        ? {
            amount: row.amount_minor ? String(minorToMajor(row.amount_minor)) : "",
            stripePriceId: row.stripe_price_id || "",
            status: row.status || "draft",
          }
        : emptyPriceDraft();
    }
    return drafts;
  }

  const selected = tariffs.find((card) => card.id === selectedId) || null;
  const isEdit = selectedId !== null;
  const canDelete = isEdit && selected && selected.legacy_tier == null;

  const loadTariffs = async () => {
    setLoading(true);
    try {
      const headers = authHeaders();
      const res = await axios.get(api(""), { headers });
      setTariffs(res.data.tariffs || []);
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTariffs();
  }, []);

  useEffect(() => {
    setPriceDrafts(draftsFromTariff(selected));
  }, [selectedId, selected]);

  useEffect(() => {
    setDetailTab("edit");
  }, [selectedId]);

  const clearFormFields = () => {
    setName("");
    setDescription("");
    setBadge("");
    setCredits("");
    setFeaturesText("");
    setSortOrder("");
    setStatus("active");
  };

  const startCreate = () => {
    setSelectedId(null);
    setCreating(true);
    clearFormFields();
  };

  const exitEditor = () => {
    setSelectedId(null);
    setCreating(false);
    clearFormFields();
  };

  const selectTariff = (card) => {
    setCreating(false);
    setSelectedId(card.id);
    setName(card.name || "");
    setDescription(card.description || "");
    setBadge(card.badge || "");
    setCredits(String(card.credits_included));
    setFeaturesText((card.features || []).join("\n"));
    setSortOrder(card.sort_order === 0 || card.sort_order ? String(card.sort_order) : "");
    setStatus(card.status || "active");
    setPriceDrafts(draftsFromTariff(card));
  };

  const buildPayload = () => {
    const payload = {
      name,
      description,
      badge,
      features: featuresText.split("\n"),
      status,
    };
    if (credits.trim() !== "") payload.credits_included = Number(credits);
    if (sortOrder.trim() !== "") payload.sort_order = Number(sortOrder);
    return payload;
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const headers = authHeaders();
      const payload = buildPayload();
      if (selectedId === null) {
        await axios.post(api(""), payload, { headers });
        await loadTariffs();
        exitEditor();
      } else {
        await axios.patch(api("/" + selectedId), payload, { headers });
        await loadTariffs();
      }
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete tariff "${name}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const headers = authHeaders();
      await axios.delete(api("/" + selectedId), { headers });
      await loadTariffs();
      exitEditor();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setSaving(false);
    }
  };

  const savePriceRow = async (code) => {
    const draft = priceDrafts[code] || emptyPriceDraft();
    const amountMinor = majorToMinor(draft.amount);
    if (draft.status === "published" && (amountMinor === null || amountMinor === 0)) {
      toast.error("Amount is required to publish");
      return;
    }
    setRowSaving(code);
    try {
      await axios.put(
        api(`/${selectedId}/prices/${code}`),
        {
          amount_minor: amountMinor ?? 0,
          stripe_price_id: String(draft.stripePriceId || "").trim() || null,
          status: draft.status,
        },
        { headers: authHeaders() },
      );
      await loadTariffs();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setRowSaving(null);
    }
  };

  const clearPriceRow = async (code) => {
    if (!window.confirm(`Clear price row for "${code}"? This cannot be undone.`)) return;
    setRowSaving(code);
    try {
      await axios.delete(api(`/${selectedId}/prices/${code}`), { headers: authHeaders() });
      await loadTariffs();
    } catch (err) {
      toast.error(apiErrorDetail(err));
    } finally {
      setRowSaving(null);
    }
  };

  const inputClass =
    "w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-sm text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#00D1FF]/50";

  const tabTriggerClass =
    "data-[state=active]:bg-[#00D1FF] data-[state=active]:text-[#0F1115] rounded-md py-2.5 font-heading font-semibold transition-all";

  const tariffForm = (
    <div className="max-w-2xl space-y-4">
      <label className="block text-sm text-[#9CA3AF]">
        Name
        <input
          className={`${inputClass} mt-1`}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Description
        <textarea
          className={`${inputClass} mt-1 min-h-[80px]`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Badge
        <input
          className={`${inputClass} mt-1`}
          value={badge}
          onChange={(e) => setBadge(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Credits
        <input
          className={`${inputClass} mt-1`}
          value={credits}
          onChange={(e) => setCredits(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Features
        <textarea
          className={`${inputClass} mt-1 min-h-[80px]`}
          value={featuresText}
          onChange={(e) => setFeaturesText(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Sort order
        <input
          className={`${inputClass} mt-1`}
          value={sortOrder}
          onChange={(e) => setSortOrder(e.target.value)}
        />
      </label>
      <label className="block text-sm text-[#9CA3AF]">
        Status
        <select
          className={`${inputClass} mt-1`}
          value={status}
          onChange={(e) => setStatus(e.target.value)}
        >
          <option value="active">active</option>
          <option value="hidden">hidden</option>
        </select>
      </label>
      <button
        type="button"
        disabled={saving}
        onClick={handleSave}
        className="px-4 py-2 rounded-lg bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20 text-sm disabled:opacity-50"
      >
        Save
      </button>
      {canDelete ? (
        <div className="flex flex-col gap-3 pt-2 border-t border-white/10">
          <button
            type="button"
            disabled={saving}
            onClick={handleDelete}
            className="px-4 py-2 rounded-lg border border-red-500/30 text-sm text-red-400 disabled:opacity-50"
          >
            Delete
          </button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div data-testid="admin-tariffs">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-2xl font-bold mb-2">Tariffs</h1>
          <p className="text-[#9CA3AF] text-sm">Manage tariff products (prices come later)</p>
        </div>
        <button
          type="button"
          onClick={startCreate}
          className="px-4 py-2 rounded-lg border border-white/10 text-sm text-[#F3F4F6] hover:bg-white/5"
        >
          New tariff
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {loading ? (
          <p className="text-[#9CA3AF]">Loading…</p>
        ) : tariffs.length === 0 ? (
          <p className="text-[#9CA3AF]">No tariffs yet</p>
        ) : (
          tariffs.map((card) => (
            <div
              key={card.id}
              role="button"
              tabIndex={0}
              onClick={() => selectTariff(card)}
              onKeyDown={(e) => {
                if (e.key === "Enter") selectTariff(card);
              }}
              className={`w-full text-left rounded-xl border p-4 bg-white/[0.03] transition-colors cursor-pointer ${
                selectedId === card.id
                  ? "border-[#00D1FF]/40"
                  : "border-white/10 hover:border-white/20"
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-heading text-[#F3F4F6]">{card.name}</span>
                {card.badge ? (
                  <span className="text-xs px-2 py-0.5 rounded bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20">
                    {card.badge}
                  </span>
                ) : null}
              </div>
              <p className="text-xs text-[#9CA3AF]">
                Credits: {card.credits_included} · Status: {card.status}
              </p>
              {card.legacy_tier != null ? (
                <p className="text-xs text-[#00D1FF] mt-1">Legacy tier {card.legacy_tier}</p>
              ) : null}
            </div>
          ))
        )}
      </div>

      <div className="mt-6">
        {isEdit ? (
          <Tabs value={detailTab} onValueChange={setDetailTab} className="w-full">
            <TabsList className="grid w-full grid-cols-2 h-auto bg-white/5 p-1 rounded-lg">
              <TabsTrigger value="edit" className={tabTriggerClass}>
                Edit tariff
              </TabsTrigger>
              <TabsTrigger value="prices" className={tabTriggerClass}>
                Country prices
              </TabsTrigger>
            </TabsList>
            <TabsContent value="edit" className="mt-4">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5">
                {tariffForm}
              </div>
            </TabsContent>
            <TabsContent value="prices" className="mt-4">
              <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4 overflow-x-auto">
                <p className="text-sm text-[#9CA3AF]">
                  Price ID must come from the primary Stripe account. Tax is included. Client Billing
                  still uses old USD prices until cutover.
                </p>
                <table className="w-full text-sm text-left">
                  <thead>
                    <tr className="text-[#9CA3AF] border-b border-white/10">
                      <th className="py-2 pr-3 font-medium">Country</th>
                      <th className="py-2 pr-3 font-medium">Currency</th>
                      <th className="py-2 pr-3 font-medium">Amount</th>
                      <th className="py-2 pr-3 font-medium">Price ID</th>
                      <th className="py-2 pr-3 font-medium">Status</th>
                      <th className="py-2 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SELLING_COUNTRIES.map((code) => {
                      const draft = priceDrafts[code] || emptyPriceDraft();
                      const hasServerRow = (selected?.prices || []).some((p) => p.country === code);
                      return (
                        <tr
                          key={code}
                          data-testid={`admin-tariff-price-${code}`}
                          className="border-b border-white/10 last:border-0"
                        >
                          <td className="py-2 pr-3 text-[#F3F4F6] whitespace-nowrap">
                            {COUNTRY_LABELS[code]} ({code})
                          </td>
                          <td className="py-2 pr-3 text-[#9CA3AF] uppercase">
                            {currencyForCountry(code)}
                          </td>
                          <td className="py-2 pr-3">
                            <input
                              className={inputClass}
                              value={draft.amount}
                              onChange={(e) => patchDraft(code, { amount: e.target.value })}
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <input
                              className={inputClass}
                              value={draft.stripePriceId}
                              onChange={(e) =>
                                patchDraft(code, { stripePriceId: e.target.value })
                              }
                            />
                          </td>
                          <td className="py-2 pr-3">
                            <select
                              className={inputClass}
                              value={draft.status}
                              onChange={(e) => patchDraft(code, { status: e.target.value })}
                            >
                              <option value="draft">draft</option>
                              <option value="published">published</option>
                            </select>
                          </td>
                          <td className="py-2">
                            <div className="flex items-center gap-2 whitespace-nowrap">
                              <button
                                type="button"
                                disabled={rowSaving === code}
                                onClick={() => savePriceRow(code)}
                                className="px-3 py-1.5 rounded-lg bg-[#00D1FF]/10 text-[#00D1FF] border border-[#00D1FF]/20 text-xs disabled:opacity-50"
                              >
                                Save
                              </button>
                              {hasServerRow ? (
                                <button
                                  type="button"
                                  disabled={rowSaving === code}
                                  onClick={() => clearPriceRow(code)}
                                  className="px-3 py-1.5 rounded-lg border border-white/10 text-xs text-[#F3F4F6] hover:bg-white/5 disabled:opacity-50"
                                >
                                  Clear
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        ) : creating ? (
          <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 space-y-4">
            <h2 className="font-heading text-lg text-[#F3F4F6]">Create tariff</h2>
            {tariffForm}
          </div>
        ) : (
          <p className="text-sm text-[#9CA3AF]">Select a tariff to edit</p>
        )}
      </div>
    </div>
  );
};

export default AdminTariffs;
